import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { academyAdminAPI } from '../../lib/academyApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Award, Download, Trash2, FileText, Plus, UserCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AcademyCertificates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [showGenerator, setShowGenerator] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [cRes, rRes] = await Promise.all([academyAdminAPI.listCertificates(), academyAdminAPI.listRegistrations()]);
      setCertificates(cRes.data);
      setRegistrations(rRes.data.filter(r => !r.certificate_generated && ['paid', 'completed', 'registered', 'approved'].includes(r.status)));
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleGenerate = async (regId) => {
    setGenerating(regId);
    try {
      await academyAdminAPI.generateCertificate(regId);
      toast.success('Certificat genere');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de generation'); }
    finally { setGenerating(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce certificat?')) return;
    try { await academyAdminAPI.deleteCertificate(id); toast.success('Certificat supprime'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#F9B438]" /></div>;

  return (
    <div className="space-y-6" data-testid="academy-certificates-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold text-[#1D1D1B]">Certificats</h1><p className="text-sm text-neutral-500 mt-1">{certificates.length} certificat(s) emis</p></div>
        {registrations.length > 0 && <Button onClick={() => setShowGenerator(!showGenerator)} data-testid="toggle-generator-btn"><Plus className="w-4 h-4 mr-2" />Generer ({registrations.length})</Button>}
      </div>

      {/* Pending registrations for certificate generation */}
      {showGenerator && registrations.length > 0 && (
        <Card className="border-l-4 border-l-[#F9B438]">
          <CardContent className="p-4">
            <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><UserCheck className="w-4 h-4 text-[#F9B438]" />Inscriptions eligibles</h3>
            <div className="space-y-2">
              {registrations.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg" data-testid={`eligible-reg-${r.id}`}>
                  <div>
                    <p className="font-medium text-sm">{r.first_name || r.name?.split(' ')[0]} {r.last_name || r.name?.split(' ').slice(1).join(' ')}</p>
                    <p className="text-xs text-neutral-400">{r.training_title} - {r.email}</p>
                  </div>
                  <Button size="sm" onClick={() => handleGenerate(r.id)} disabled={generating === r.id} data-testid={`generate-cert-${r.id}`}>
                    {generating === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Award className="w-3 h-3 mr-1" />Generer</>}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Issued certificates */}
      {certificates.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Award className="w-10 h-10 text-neutral-200 mx-auto mb-3" /><p className="text-neutral-400">Aucun certificat emis</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {certificates.map(cert => (
            <Card key={cert.id} className="hover:shadow-sm transition-shadow" data-testid={`certificate-${cert.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}>
                  <Award className="w-6 h-6 text-[#F9B438]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#1D1D1B]">{cert.participant_name}</h3>
                  <p className="text-xs text-neutral-400 truncate">{cert.course_title}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                    <span>Formateur: {cert.instructor_name}</span>
                    <span>{cert.duration_hours}h</span>
                    <span>{cert.session_date}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {cert.certificate_url && (
                    <a href={`${API_URL}${cert.certificate_url}`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
                    </a>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => handleDelete(cert.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AcademyCertificates;
